import { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  CASE_REALTIME_TRANSCRIPT_MESSAGE_RECEIVED,
  CASE_REALTIME_TRANSCRIPT_CLEAR,
  CASE_REALTIME_REINDEX_RANGE_CLEAR,
} from './constants';

const MAX_REALTIME_LINES = 2000;

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const makeLineId = () => `rt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const findLineIndex = (lines, entry) =>
  lines.findIndex((line) => line.lineId === entry.lineId);

// Helper: apply a ReflowApplied bulk event to a state snapshot.
// Expects server fields: lineNumber, pageNumber, pageLineNumber, text (post-rename).
function applyReflowApplied(state, event, sessionKey) {
  const sk = sessionKey ?? state.realTimeTranscriptSessionKey;
  const range = event.affectedRange;
  const makeReflowId = (lineNo) =>
    sk ? `${sk}_${String(lineNo).padStart(6, '0')}` : makeLineId();

  // Track the open line's lineNumber before we replace anything.
  // If the reflow range covers the open line, preserve isFinal:false on it
  // so the cursor stays at the bottom of the transcript.
  const openLineNumber = state.realTimeTranscriptLines.find(
    (l) => l.isFinal === false,
  )?.lineNumber ?? null;

  const newLines = (event.lines ?? []).map((l) => ({
    id: makeReflowId(l.lineNumber),
    lineId: makeReflowId(l.lineNumber),
    lineNumber: l.lineNumber,
    pageNumber: l.pageNumber,
    pageLineNumber: l.pageLineNumber,
    text: l.text ?? '',
    type: 'reflowapplied',
    isFinal: l.lineNumber === openLineNumber ? false : true,
  }));

  let nextLines = state.realTimeTranscriptLines.slice();
  if (range) {
    nextLines = nextLines.filter(
      (l) =>
        l.lineNumber == null ||
        l.lineNumber < range.fromLine ||
        l.lineNumber > range.toLine,
    );
    const insertIdx = nextLines.findIndex(
      (l) => l.lineNumber != null && l.lineNumber >= range.fromLine,
    );
    if (insertIdx >= 0) nextLines.splice(insertIdx, 0, ...newLines);
    else nextLines.push(...newLines);
  } else {
    nextLines.push(...newLines);
  }

  return {
    ...state,
    realTimeTranscriptLines: nextLines,
    realTimeTranscriptLastEventAt: Date.now(),
  };
}

// Helper: apply a MultiCorrected bulk event to a state snapshot.
// Expects server fields: lineNumber, text, delta (post-rename).
function applyMultiCorrected(state, event) {
  const nextLines = state.realTimeTranscriptLines.slice();
  (event.corrections ?? []).forEach((c) => {
    const idx = nextLines.findIndex((l) => l.lineNumber === c.lineNumber);
    if (idx < 0) return;
    const base = nextLines[idx].text ?? '';
    const newText = (() => {
      // Prefer full replacement text — always safe regardless of local state.
      // Fall back to delta only when text is absent.
      if (c.text != null) return c.text;
      const { delta } = c;
      if (!delta) return base;
      const offset = Math.max(0, Math.min(delta.offset, base.length));
      const remove = Math.max(0, delta.remove);
      return base.slice(0, offset) + (delta.insert ?? '') + base.slice(offset + remove);
    })();
    // Preserve the existing open/closed state — never move the cursor.
    // If this line is the active open line it stays open; if it's a previous
    // finalized line it stays finalized.
    nextLines[idx] = { ...nextLines[idx], text: newText, type: 'multicorrected' };
  });
  return {
    ...state,
    realTimeTranscriptLines: nextLines,
    realTimeTranscriptLastEventAt: Date.now(),
  };
}

// Helper: apply an array of individual line events (LineFinalized / LineCorrected).
function applyLineEvents(state, entries, msgSessionKey) {
  const sessionKey = state.realTimeTranscriptSessionKey || msgSessionKey;
  let nextLines = state.realTimeTranscriptLines.slice();

  entries.forEach((entry) => {
    const isFinalized = entry.type === 'LineFinalized';
    const isCorrected = entry.type === 'LineCorrected';
    const incomingIsFinal = isFinalized ? true : isCorrected ? false : undefined;

    const targetIndex = findLineIndex(nextLines, entry);

    if (targetIndex >= 0) {
      const existing = nextLines[targetIndex];
      const shouldOverride =
        entry.version != null && existing.version != null
          ? entry.version >= existing.version
          : !(incomingIsFinal === false && existing.isFinal === true);

      if (!shouldOverride) return;

      // LineCorrected never changes the open/closed state of a line.
      // If the line is already closed (isFinal:true — a previous line), it stays
      // closed so the cursor does not move back to it.
      // If the line is open (isFinal:false — the active last line), it stays
      // open so shouldAnimateLive remains true and the text animates.
      // LineFinalized on an existing open line closes it explicitly.
      const resolvedIsFinal =
        isCorrected
          ? existing.isFinal
          : (incomingIsFinal ?? existing.isFinal);

      let newText;
      if (entry.delta) {
        const { offset, remove, insert } = entry.delta;
        const base = existing.text ?? '';
        newText = base.slice(0, offset) + (insert ?? '') + base.slice(offset + remove);
      } else {
        newText = entry.text ?? existing.text ?? '';
      }

      nextLines[targetIndex] = {
        ...existing,
        ...entry,
        id: existing.id,
        text: newText,
        isFinal: resolvedIsFinal,
        lineId: entry.lineId ?? existing.lineId,
        lineNumber: entry.lineNumber ?? existing.lineNumber,
        pageNumber: entry.pageNumber ?? existing.pageNumber,
        pageLineNumber: entry.pageLineNumber ?? existing.pageLineNumber,
        version: entry.version ?? existing.version,
        observedAtUtc: entry.observedAtUtc ?? existing.observedAtUtc ?? null,
        finalizedAtUtc: entry.finalizedAtUtc ?? existing.finalizedAtUtc ?? null,
      };
    } else {
      const newLine = {
        ...entry,
        id: entry.lineId ? String(entry.lineId) : String(entry.lineNumber ?? makeLineId()),
        lineId: entry.lineId ? String(entry.lineId) : undefined,
        text: entry.text ?? '',
        // New lines always start open (isFinal:false). The close-previous-open
        // logic below closes the previous active line when this one is appended.
        // This ensures LineFinalized messages for the active line animate correctly
        // via the shouldAnimateLive path before the next line arrives.
        isFinal: false,
      };
      const hasIdentity =
        newLine.lineId != null || newLine.lineNumber != null || newLine.pageLineNumber != null;
      const hasNonEmptyText = newLine.text.length > 0;
      if (hasNonEmptyText || hasIdentity) {
        // Only one line can be open at a time. Close any previously open line
        // before appending a new partial one.
        if (newLine.isFinal === false) {
          for (let i = 0; i < nextLines.length; i += 1) {
            if (nextLines[i].isFinal === false) {
              nextLines[i] = { ...nextLines[i], isFinal: true };
            }
          }
        }
        nextLines.push(newLine);
      }
    }
  });

  if (nextLines.length > MAX_REALTIME_LINES) {
    nextLines.splice(0, nextLines.length - MAX_REALTIME_LINES);
  }

  return {
    ...state,
    realTimeTranscriptLines: nextLines,
    realTimeTranscriptLastEventAt: Date.now(),
    realTimeTranscriptSessionKey: sessionKey,
  };
}

export function receiveRealTimeTranscriptMessage(payload) {
  return {
    type: CASE_REALTIME_TRANSCRIPT_MESSAGE_RECEIVED,
    data: payload,
  };
}

export function clearRealTimeTranscript() {
  return {
    type: CASE_REALTIME_TRANSCRIPT_CLEAR,
  };
}

export function clearReindexRange() {
  return {
    type: CASE_REALTIME_REINDEX_RANGE_CLEAR,
  };
}

export function useRealTimeTranscript() {
  const dispatch = useDispatch();
  const {
    lines,
    lastEventAt,
    sessionKey,
    sessionRestartedAt,
    sessionNotificationType,
    restartSessionKey,
    noteNotifications,
    reindexRangePending,
  } = useSelector(
    (state) => ({
      lines: state.case.realTimeTranscriptLines || [],
      lastEventAt: state.case.realTimeTranscriptLastEventAt || null,
      sessionKey: state.case.realTimeTranscriptSessionKey || null,
      sessionRestartedAt: state.case.realTimeTranscriptSessionRestartedAt || null,
      sessionNotificationType: state.case.realTimeTranscriptSessionNotificationType || null,
      restartSessionKey: state.case.realTimeTranscriptRestartSessionKey || null,
      noteNotifications: state.case.realTimeNoteNotifications || [],
      reindexRangePending: state.case.realTimeReindexRangePending || null,
    }),
    shallowEqual,
  );

  const clear = useCallback(() => {
    dispatch(clearRealTimeTranscript());
  }, [dispatch]);

  const dispatchClearReindexRange = useCallback(() => {
    dispatch(clearReindexRange());
  }, [dispatch]);

  return {
    lines,
    lastEventAt,
    sessionKey,
    sessionRestartedAt,
    sessionNotificationType,
    restartSessionKey,
    noteNotifications,
    reindexRangePending,
    clear,
    clearReindexRange: dispatchClearReindexRange,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_REALTIME_TRANSCRIPT_MESSAGE_RECEIVED: {
      if (!isPlainObject(action.data)) return state;

      const { messageType, payload, metadata } = action.data;
      const msgSessionKey = metadata?.sessionKey ?? null;
      const msgCaseId = metadata?.caseId ?? null;

      switch (messageType) {
        case 'rt_session_notification': {
          if (
            payload === 'new_session_started' ||
            payload === 'session_restarted' ||
            payload === 'session_resumed'
          ) {
            return {
              ...state,
              realTimeTranscriptSessionRestartedAt: Date.now(),
              realTimeTranscriptSessionNotificationType: payload,
              realTimeTranscriptRestartSessionKey: msgSessionKey ? String(msgSessionKey) : null,
            };
          }
          // 'session_ended' and other future notifications — no action
          return state;
        }

        case 'rt_note_notification': {
          if (!isPlainObject(payload)) return state;
          return {
            ...state,
            realTimeNoteNotifications: [
              ...(state.realTimeNoteNotifications || []),
              {
                payload,
                sessionKey: msgSessionKey ? String(msgSessionKey) : '',
                caseId: msgCaseId ? String(msgCaseId) : '',
                receivedAt: Date.now(),
              },
            ],
          };
        }

        case 'realtime_line_event': {
          if (!Array.isArray(payload)) {
            if (!isPlainObject(payload)) return state;
            const bulkType = payload.type?.toLowerCase();
            if (bulkType === 'reindexrange') {
              return { ...state, realTimeReindexRangePending: { requestedAt: Date.now() } };
            }
            if (bulkType === 'reflowapplied') {
              return applyReflowApplied(state, payload, msgSessionKey);
            }
            if (bulkType === 'multicorrected') {
              return applyMultiCorrected(state, payload);
            }
            return state;
          }
          if (!payload.length) return state;
          return applyLineEvents(state, payload, msgSessionKey);
        }

        default:
          return state;
      }
    }
    case CASE_REALTIME_REINDEX_RANGE_CLEAR:
      return { ...state, realTimeReindexRangePending: null };
    case CASE_REALTIME_TRANSCRIPT_CLEAR:
      return {
        ...state,
        realTimeTranscriptLines: [],
        realTimeTranscriptLastEventAt: null,
        realTimeTranscriptSessionKey: null,
        realTimeTranscriptSessionRestartedAt: null,
        realTimeTranscriptSessionNotificationType: null,
        realTimeTranscriptRestartSessionKey: null,
        realTimeReindexRangePending: null,
        realTimeNoteNotifications: [],
      };
    default:
      return state;
  }
}
